import type {
  CreateOpenaiSessionArgs,
  CreateOpenaiSessionRequest,
  CreateOpenaiSessionResponse,
  CreateTranscriptAnnotationArgs,
  CreateTranscriptAnnotationRequest,
  CreateTranscriptAnnotationResponse,
  CreateTranscriptMessageArgs,
  CreateTranscriptMessageRequest,
  CreateTranscriptMessageResponse,
  CreateVoiceFeedbackArgs,
  CreateVoiceFeedbackResponse,
  CreateVoiceSessionTraceArgs,
  CreateVoiceSessionTraceResponse,
  GetToolMediaArgs,
  GetToolMediaResponse,
  ListVoiceToolCallsResponse,
  PersistedVoiceToolCall,
  ReportTranscriptAnnotationFailureArgs,
  ReportTranscriptAnnotationFailureRequest,
  RunGraphqlToolsArgs,
  RunGraphqlToolsRequest,
  RunGraphqlToolsResponse,
  RunToolArgs,
  RunToolRequest,
  RunToolResponse,
  RunToolRunningResponse,
  VoiceAudioProfile,
  VoiceChatSession,
  VoiceGraphqlToolResult,
  VoiceToolMedia,
  VoiceToolResult,
  VoiceTranscriptPersistenceResult,
} from "@/types/voice-chat"

import { buildClientAxios } from "@/lib/client-axios"
import { baseApi } from "./base-api"

export async function reportTranscriptAnnotationFailure({
  chatId,
  openaiCallId,
  providerEventId,
  annotationResponseId,
  sourceResponseId,
  validationError,
  rawText,
  rawTextTruncated,
}: ReportTranscriptAnnotationFailureArgs): Promise<void> {
  const data: ReportTranscriptAnnotationFailureRequest = {
    chat_id: chatId,
    openai_call_id: openaiCallId,
    provider_event_id: providerEventId,
    annotation_response_id: annotationResponseId,
    source_response_id: sourceResponseId,
    validation_error: validationError,
    raw_text: rawText,
    raw_text_truncated: rawTextTruncated,
  }

  await buildClientAxios().post(
    "/api/voice_chat/transcript_annotation_failures",
    data
  )
}

export const voiceChatApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    createVoiceFeedback: builder.mutation<
      CreateVoiceFeedbackResponse,
      CreateVoiceFeedbackArgs
    >({
      query: ({ chatId, thumbsUp, comment, messageId, trace }) => ({
        url: "/api/voice_chat/feedback",
        method: "POST",
        // One tap is one upload: a blocked or dropped trace upload should not
        // turn into five attempts and five trace errors.
        retries: 0,
        headers: { "content-type": "multipart/form-data" },
        data: {
          chat_id: chatId,
          thumbs_up: thumbsUp,
          comment,
          message_id: messageId,
          voice_session_ids: trace?.voiceSessionIds,
          content_encoding: trace?.contentEncoding,
          trace: trace?.file,
        },
      }),
    }),
    createSessionTrace: builder.mutation<
      CreateVoiceSessionTraceResponse,
      CreateVoiceSessionTraceArgs
    >({
      query: ({
        chatId,
        voiceSessionId,
        flushReason,
        contentEncoding,
        entryCount,
        droppedCount,
        trace,
        phantomBargeInCount,
        assistantAudioMs,
        turnDetectionType,
        noiseReduction,
      }) => ({
        url: "/api/voice_chat/session_traces",
        method: "POST",
        retries: 0,
        headers: { "content-type": "multipart/form-data" },
        data: {
          chat_id: chatId,
          voice_session_id: voiceSessionId,
          flush_reason: flushReason,
          content_encoding: contentEncoding,
          entry_count: entryCount,
          dropped_count: droppedCount,
          trace,
          phantom_barge_in_count: phantomBargeInCount,
          assistant_audio_ms: assistantAudioMs,
          turn_detection_type: turnDetectionType,
          noise_reduction: noiseReduction,
        },
      }),
    }),
    createOpenaiSession: builder.mutation<
      VoiceChatSession,
      CreateOpenaiSessionArgs
    >({
      query: ({
        chatId,
        sdpOffer,
        speed,
        reasoningEffort,
        entityRef,
        signal,
      }) => {
        // turn_detection and noise_reduction are override-only fields, still honored
        // for shipped iOS. The web sends neither, so every web session runs on the
        // audio profile the server resolved.
        const data: CreateOpenaiSessionRequest = {
          chat_id: chatId,
          sdp_offer: sdpOffer,
          speed,
          reasoning_effort: reasoningEffort,
          voice_startup_protocol: "collapsed_history_v1",
          // The call opens with a start sound, not a spoken greeting, so the
          // session instructions must not carry greeting rules that would leak
          // into the customer's first real answer. Omitting this keeps them
          // (the iOS client still greets).
          greet_on_open: false,
          entity_ref: entityRef ?? undefined,
        }

        return {
          url: "/api/voice_chat/openai/sessions",
          method: "POST",
          data,
          signal,
        }
      },
      transformResponse: (
        response: CreateOpenaiSessionResponse
      ): VoiceChatSession => ({
        sdpAnswer: response.sdp_answer,
        openaiCallId: response.openai_call_id,
        historyItems: response.history_items,
        toolProgressLabels: Object.fromEntries(
          (response.tools ?? []).map((tool) => [tool.name, tool.progress_label])
        ),
        contextOnlyToolNames: response.context_only_tool_names ?? [],
        turnDetectionType: response.turn_detection.type,
        noiseReduction: response.noise_reduction,
      }),
    }),
    getAudioProfile: builder.query<VoiceAudioProfile, void>({
      query: () => ({ url: "/api/voice/audio_profile" }),
    }),
    createTranscriptAnnotation: builder.mutation<
      CreateTranscriptAnnotationResponse,
      CreateTranscriptAnnotationArgs
    >({
      query: ({
        chatId,
        openaiCallId,
        providerEventId,
        messageId,
        transcript,
        candidates,
        toolCallIds,
      }) => {
        const data: CreateTranscriptAnnotationRequest = {
          chat_id: chatId,
          openai_call_id: openaiCallId,
          provider_event_id: providerEventId,
          message_id: messageId,
          transcript,
          tool_call_ids: toolCallIds,
          candidates: candidates.map((candidate) => ({
            original_text: candidate.originalText,
            name: candidate.name,
            type: candidate.type,
            location: candidate.location,
            proper_noun: candidate.properNoun,
            entity_ref: candidate.entityRef,
          })),
        }

        return {
          url: "/api/voice_chat/transcript_annotations",
          method: "POST",
          data,
        }
      },
    }),
    createTranscriptMessage: builder.mutation<
      VoiceTranscriptPersistenceResult,
      CreateTranscriptMessageArgs
    >({
      query: ({
        chatId,
        role,
        transcript,
        providerEventId,
        providerEventIds,
        toolCallIds,
        openaiCallId,
        interrupted,
        fullTranscript,
        truncatedAtMs,
        cutoffReason,
      }) => {
        const data: CreateTranscriptMessageRequest = {
          chat_id: chatId,
          role,
          transcript,
          provider_event_id: providerEventId,
          provider_event_ids: providerEventIds,
          tool_call_ids: toolCallIds,
          openai_call_id: openaiCallId,
          interrupted,
          full_transcript: fullTranscript,
          truncated_at_ms: truncatedAtMs,
          cutoff_reason: cutoffReason,
        }

        return {
          url: "/api/voice_chat/transcript_messages",
          method: "POST",
          data,
        }
      },
      transformResponse: (
        response: CreateTranscriptMessageResponse
      ): VoiceTranscriptPersistenceResult =>
        mapVoiceTranscriptPersistenceResponse(response),
    }),
    runTool: builder.mutation<VoiceToolResult, RunToolArgs>({
      query: ({
        chatId,
        openaiCallId,
        userMessageId,
        callId,
        toolName,
        arguments: toolArguments,
        completedSkillCallIds,
      }) => {
        const data: RunToolRequest = {
          chat_id: chatId,
          openai_call_id: openaiCallId,
          user_message_id: userMessageId,
          call_id: callId,
          tool_name: toolName,
          arguments: toolArguments,
          completed_skill_call_ids: completedSkillCallIds,
        }

        return {
          url: "/api/voice_chat/tool_calls",
          method: "POST",
          data,
        }
      },
      transformResponse: (response: RunToolResponse): VoiceToolResult =>
        mapRunToolResponse(response),
    }),
    runGraphqlTools: builder.mutation<
      VoiceGraphqlToolResult[],
      RunGraphqlToolsArgs
    >({
      query: ({
        chatId,
        openaiCallId,
        userMessageId,
        responseId,
        completedSkillCallIds,
        toolCalls,
      }) => {
        const data: RunGraphqlToolsRequest = {
          chat_id: chatId,
          openai_call_id: openaiCallId,
          user_message_id: userMessageId,
          response_id: responseId,
          completed_skill_call_ids: completedSkillCallIds,
          tool_calls: toolCalls.map((toolCall) => ({
            call_id: toolCall.callId,
            output_index: toolCall.outputIndex,
            arguments: toolCall.arguments,
          })),
        }

        return {
          url: "/api/voice_chat/graphql_tool_calls",
          method: "POST",
          data,
        }
      },
      transformResponse: (
        response: RunGraphqlToolsResponse
      ): VoiceGraphqlToolResult[] =>
        response.tool_calls.map((entry) => ({
          callId: entry.call_id,
          result: mapRunToolResponse(entry.result),
        })),
    }),
    getToolResult: builder.query<
      VoiceToolResult,
      { chatId: number; openaiCallId: string; handle: string }
    >({
      query: ({ chatId, openaiCallId, handle }) => ({
        url: `/api/voice_chat/tool_calls/${handle}`,
        method: "GET",
        params: {
          chat_id: chatId,
          openai_call_id: openaiCallId,
        },
      }),
      transformResponse: (response: RunToolResponse): VoiceToolResult =>
        mapRunToolResponse(response),
    }),
    getToolMedia: builder.query<VoiceToolMedia[], GetToolMediaArgs>({
      query: ({ chatId, openaiCallId, callId }) => ({
        url: `/api/voice_chat/tool_calls/${callId}/media`,
        method: "GET",
        params: {
          chat_id: chatId,
          openai_call_id: openaiCallId,
        },
      }),
      transformResponse: (response: GetToolMediaResponse) =>
        response.media.map((media) => ({
          mimeType: media.mime_type,
          dataUri: media.data_uri,
          byteCount: media.byte_count,
        })),
    }),
    listToolCalls: builder.query<PersistedVoiceToolCall[], number>({
      query: (chatId) => ({
        url: "/api/voice_chat/tool_calls",
        method: "GET",
        params: {
          chat_id: chatId,
        },
      }),
      transformResponse: (
        response: ListVoiceToolCallsResponse
      ): PersistedVoiceToolCall[] =>
        response.tool_calls.map((call) => ({
          callId: call.call_id,
          openaiCallId: call.openai_call_id,
          toolName: call.tool_name,
          query: call.query,
          locationHint: call.location_hint ?? null,
          status: call.status,
          candidates: call.candidates.map((candidate) => ({
            entityId: candidate.entity_id,
            type: candidate.type,
            name: candidate.name,
            rank: candidate.rank,
          })),
          errorMessage: call.error_message ?? null,
          handle: call.handle ?? null,
          timestamp: call.timestamp,
        })),
    }),
  }),
})

export const {
  useCreateVoiceFeedbackMutation,
  useCreateOpenaiSessionMutation,
  useCreateTranscriptAnnotationMutation,
  useCreateTranscriptMessageMutation,
  useRunToolMutation,
  useLazyGetToolResultQuery,
  useListToolCallsQuery,
} = voiceChatApi

export function mapVoiceTranscriptPersistenceResponse(
  response: CreateTranscriptMessageResponse
): VoiceTranscriptPersistenceResult {
  if (response.deleted === true) {
    return {
      deleted: true,
      messageId: response.message_id,
      body: response.body,
      markers: response.markers,
      places: response.places,
      detectedLanguage: response.detected_language,
    }
  }

  return {
    messageId: response.message_id,
    markers: response.markers,
    places: response.places,
    body: response.body,
    detectedLanguage: response.detected_language,
    // The wire flag is only half an address — the persisted message id is what
    // the summary is fetched by, so the two collapse into one field here.
    flightSummaryMessageId: response.has_flight_search_summary
      ? response.message_id
      : undefined,
  }
}

export function mapRunToolResponse(response: RunToolResponse): VoiceToolResult {
  if (isRunToolRunningResponse(response)) {
    return {
      status: "running",
      handle: response.handle,
      objectType: response.object_type,
    }
  }

  return {
    results: response.results,
    speechResults: response.speech_results,
    isError: response.is_error,
    images: response.images,
    entityMap: response.entity_map,
    hotelQuoteRefs: response.hotel_quote_refs,
    error: response.error,
    responseConfiguration: response.response_configuration && {
      tools: response.response_configuration.tools,
      callIds: response.response_configuration.call_ids,
      toolProgressLabels: response.response_configuration.tool_progress_labels,
      graphqlProgressLabels:
        response.response_configuration.graphql_progress_labels,
    },
    additionalToolCalls: response.additional_tool_calls?.map((call) => ({
      callId: call.call_id,
      name: call.name,
      arguments: call.arguments,
      output: call.output,
    })),
  }
}

function isRunToolRunningResponse(
  response: RunToolResponse
): response is RunToolRunningResponse {
  return "status" in response && response.status === "running"
}
